import { useAccess } from '../auth/access';
import { useQualityBackfill } from '../hooks/useQualityBackfill';
import { setStarMetadataFill, useStarMetadataFill } from '../hooks/useStarMetadataFill';

export default function QualityBackfillControls({
  dbId,
  canManage = true,
}: {
  dbId: string;
  /** Saving the arrival option needs database management on this server. */
  canManage?: boolean;
}) {
  const job = useQualityBackfill(dbId);
  const { canWrite } = useAccess();
  const progress = job.status?.progress;
  // One preference for every analyze action (scan, backfill, import), so the
  // checkbox here is also the remembered default.
  const fillMetadata = useStarMetadataFill();

  // Shown while a run is under way too: that run may be this option at work.
  const arrivals = (
    <div className="quality-backfill-option">
      <label title="Run the background quality analysis on frames that arrive by sync, peer pull, remote upload or auto-import, a couple of minutes after they land. Writes nothing to the catalog.">
        <input
          type="checkbox"
          checked={job.analyzeNewFrames}
          disabled={!canManage || job.isSavingNewFrames || !job.status}
          onChange={(event) => job.setAnalyzeNewFrames(event.target.checked)}
        />
        Analyze new frames as they arrive
      </label>
      <small>
        Sync, peer pulls, remote uploads and auto-import. Without it, new frames wait for
        Analyze Missing Quality.
        {!canManage && ' Changing it needs database management on this server.'}
      </small>
    </div>
  );

  if (job.isRunning && progress && job.isPaused) {
    return (
      <>
        <div className="quality-backfill-status" data-testid="quality-backfill-paused">
          The quality analysis is paused at {progress.processed_targets}/{progress.total_targets}{' '}
          targets until you resume it or the server restarts.{' '}
          <button
            type="button"
            className="browse-button"
            onClick={() => job.resume()}
            disabled={job.isResuming || !canWrite}
          >
            Resume
          </button>
        </div>
        {arrivals}
      </>
    );
  }

  if (job.isRunning && progress) {
    return (
      <>
        <div className="quality-backfill-status" aria-live="polite">
          Analyzing quality in the background… {progress.processed_targets}/
          {progress.total_targets} targets
        </div>
        {arrivals}
      </>
    );
  }

  return (
    <div className="quality-backfill-controls">
      <button
        type="button"
        className="browse-button"
        onClick={() => job.start(false)}
        disabled={job.isStarting}
        title="Analyze images without recomputing valid cached results"
      >
        Analyze Missing Quality
      </button>
      <button
        type="button"
        className="browse-button"
        onClick={() => {
          if (window.confirm('Recompute cached star, background, photometry, and pointing data for every image in this database?')) {
            job.start(true);
          }
        }}
        disabled={job.isStarting}
        title="Recompute star counts and all other cached quality evidence"
      >
        Rescan All Quality
      </button>
      {arrivals}
      <div className="quality-backfill-option">
        <label title="Fill measured star count and HFR into images imported without them. Only missing values are written; N.I.N.A.-recorded measurements are never replaced. Remembered as the default for every analyze action.">
          <input
            type="checkbox"
            checked={fillMetadata}
            onChange={(event) => setStarMetadataFill(event.target.checked)}
          />
          Write star count and HFR into image metadata
        </label>
        <small>
          Writes only to this catalog&apos;s database records. Your FITS and
          XISF files are never modified.
        </small>
      </div>
      {job.error && <span className="quality-backfill-error">{job.error.message}</span>}
    </div>
  );
}
